import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, untracked } from '@angular/core';
import { SidePanelContentDirective } from '@app/+authenticated/shared/app-side-panel/side-panel-content.directive';
import { SidePanelHeaderComponent } from '@app/+authenticated/shared/app-side-panel/side-panel-header.component';
import { SidePanelStateService } from '@app/+authenticated/shared/app-side-panel/side-panel-state.service';
import { SBHeaderService } from '@app/+authenticated/shared/sb-header/sb-header.service';
import { MyRequestsContentComponent } from '@app/+authenticated/shared/sidebar/my-requests-sidebar/my-requests-content.component';
import { SupervisorRequestsContentComponent } from '@app/+authenticated/shared/sidebar/supervisor-requests-sidebar/supervisor-requests-content.component';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { EMPLOYEE_POPOVER_PLACEMENT } from '@app/shared/sb-lib/employee-popover-placement.token';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { hasPermission } from '@sb/shared/permission/util';
import { PopoverPlacement } from '@sb/ui/popover';
import { TabComponent, TabGroupComponent } from '@sb/ui/tabs';

export type RequestsPanelTab = 'request' | 'my-request';

@Component({
  selector: 'requests-panel',
  templateUrl: './requests-panel.component.html',
  host: { class: 'flex h-full flex-col' },
  imports: [
    TranslatePipe,
    SidePanelHeaderComponent,
    SidePanelContentDirective,
    TabGroupComponent,
    TabComponent,
    MyRequestsContentComponent,
    SupervisorRequestsContentComponent,
  ],
  // The panel sits against the left nav rail, so employee hover-popovers inside it
  // should open right (they default to left elsewhere).
  providers: [{ provide: EMPLOYEE_POPOVER_PLACEMENT, useValue: PopoverPlacement.RIGHT }],
})
export class RequestsPanelComponent {
  #store = inject<Store<AppState>>(Store);
  #permissionState = this.#store.selectSignal(getPermissionState);
  #headerService = inject(SBHeaderService);
  #panelState = inject(SidePanelStateService);

  // Which tab to show. The side panel re-opens this same component when the requested
  // tab changes, rather than recreating it, so this must stay reactive after first render.
  tab = input<RequestsPanelTab>('my-request');

  canSeeSupervisorRequests = computed(() =>
    hasPermission(
      { userId: 'me', departments: 'any', permissions: ['Approve absentee', 'Approve exchange'] },
      this.#permissionState(),
    ),
  );

  // Tab order, mirroring the template: the supervisor ('request') tab is only
  // rendered when allowed, so the index of a given tab depends on permissions.
  #renderedTabs = computed<RequestsPanelTab[]>(() =>
    this.canSeeSupervisorRequests() ? ['request', 'my-request'] : ['my-request'],
  );

  selectedTab = linkedSignal(() => Math.max(0, this.#renderedTabs().indexOf(this.tab())));

  constructor() {
    // Keep the side panel's recorded tab in sync with what's actually showing — clicking
    // a tab inside the panel only updates `selectedTab` locally, so without this the
    // service would still think the originally-requested tab is on screen and could
    // toggle the panel closed instead of switching tabs on the next request.
    effect(() => {
      const tab = this.#renderedTabs()[this.selectedTab()];

      untracked(() => this.#panelState.updateActiveInputs('requests', { tab }));
    });

    // Reset the header request streams when the panel goes away, so the legacy request
    // sidebars (driven by the same streams) stay closed when the user toggles Labs off.
    inject(DestroyRef).onDestroy(() => {
      this.#headerService.supervisorRequestsSidebarOpen$.next(false);
      this.#headerService.myRequestsSidebarOpen$.next(false);
    });
  }
}
